
// Helper for MediaSession API
// This allows Android/iOS lock screen controls to work and sync with our app state.

interface MediaMetadataParams {
    title: string;
    artist?: string;
    album?: string;
    artwork?: { src: string; sizes?: string; type?: string }[];
}

export const updateMediaSession = (state: 'playing' | 'paused' | 'stopped', metadata?: MediaMetadataParams, handlers?: {
    onPlay?: () => void;
    onPause?: () => void;
    onStop?: () => void;
    onNext?: () => void;
    onPrev?: () => void;
}) => {
    if (!('mediaSession' in navigator)) return;

    // 1. Update State
    // Note: 'stopped' isn't a valid playbackState in all browsers, usually 'none' or 'paused'.
    // Chrome supports: 'playing', 'paused', 'none'.
    const playbackState = state === 'playing' ? 'playing' : 'paused'; // treat stopped as paused for widget
    navigator.mediaSession.playbackState = playbackState;

    // 2. Update Metadata if provided
    if (metadata) {
        navigator.mediaSession.metadata = new MediaMetadata({
            title: metadata.title,
            artist: metadata.artist || 'ClaraNarratio',
            album: metadata.album || 'claranarratio.com',
            artwork: metadata.artwork || [
                { src: '/icons/icon-192.png', sizes: '192x192', type: 'image/png' },
                { src: '/icons/icon-512.png', sizes: '512x512', type: 'image/png' }
            ]
        });
    }

    // 3. Register Handlers
    // We only register if handlers are provided, otherwise we might clear them?
    // Actually, good practice is to always register or unregister.
    // If state is 'stopped', maybe we clear listeners? 
    // But then the "Play" button on widget might not work to restart.
    // So we should keep listeners active if we want "Resume" functionality, 
    // BUT our app doesn't really support Resume from partial paragraph well yet. 
    // It just restarts current page/chunk. That's fine.

    if (handlers) {
        const { onPlay, onPause, onStop, onNext, onPrev } = handlers;

        if (onPlay) navigator.mediaSession.setActionHandler('play', onPlay);
        if (onPause) navigator.mediaSession.setActionHandler('pause', onPause);
        if (onStop) navigator.mediaSession.setActionHandler('stop', onStop);

        // Only set Next/Prev if provided (might want to disable them if no next chapter)
        if (onNext) navigator.mediaSession.setActionHandler('nexttrack', onNext);
        else navigator.mediaSession.setActionHandler('nexttrack', null);

        if (onPrev) navigator.mediaSession.setActionHandler('previoustrack', onPrev);
        else navigator.mediaSession.setActionHandler('previoustrack', null);
    } else if (state === 'stopped') {
        // Clear handlers or leave them? 
        // If we stop completely, user might want to clear widget?
        // navigator.mediaSession.playbackState = 'none'; will hide it on some devices.
        if (state === 'stopped') {
            // For now, let's just leave it PAUSED so they can hit Play again.
        }
    }
};
